import { useLocalizedPath, useSiteLocale } from "../i18n/useLocalizedPath";
import { localizeHtmlLinks } from "../i18n/localizedRoutes";
import React, { useEffect, useMemo, useRef } from "react";
import { Box, Button, useTheme } from "@chakra-ui/react";
import { useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { imageMap } from "../utils/imageMap";

// @ts-ignore
import { Chrono } from "react-chrono";
import { useNavigate } from "react-router-dom";

interface TimelineItem {
  title: string;
  cardTitle?: string;
  cardDetailedText: string;
  media?: {
    type: string;
    source: {
      url: string;
    };
  };
  // url?: string;
  link?: string;
  img?: string;
  video?: string;
}

interface TimelineComponentProps {
  items: TimelineItem[];
}

const TimelineComponent: React.FC<TimelineComponentProps> = ({ items }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const localizePath = useLocalizedPath();
  const siteLocale = useSiteLocale();
  const shouldReduceMotion = useReducedMotion();
  const timelineRef = useRef<HTMLDivElement>(null);
  const revealedCardIndexesRef = useRef<Set<number>>(new Set());

  const stopTimelineCardInteraction = (event: React.SyntheticEvent) => {
    const target = event.target;

    if (!(target instanceof Element)) return;
    if (target.closest(".timeline-read-more")) return;

    if (target.closest(".vertical-item-row")) {
      event.stopPropagation();
    }
  };

  const processedItems = useMemo(
    () =>
      items.map((item) => {
        const hasLink = item.link;
        const hasImage = item.img;
        let media = item.media;

        if (hasLink || hasImage) {
          const elements: React.ReactNode[] = [];

          elements.push(
            <span
              key="content"
              dangerouslySetInnerHTML={{ __html: localizeHtmlLinks(item.cardDetailedText, siteLocale) }}
            />
          );

          if (hasImage) {
            let imgSrc = item.img;
            if (item.img?.startsWith("http")) {
              imgSrc = item.img;
            } else {
              imgSrc = item.img ? imageMap[item.img] || item.img : item.img;
            }
            // const imageSrc = item.img?.startsWith('http') ? item.img :
            //   item.img === 'seminarPreserved' ? seminarPreserved : item.img;
            // elements.push(
            //   <Image
            //     key="image"
            //     src={imgSrc}
            //     alt="Timeline image"
            //     maxW="200px"
            //     borderRadius="md"
            //   />
            // );

            //testing
            if (imgSrc) {
              media = { type: "IMAGE", source: { url: imgSrc } };
            }
          }

          //cannot load the video
          // if (item.video) {
          //   let videoSrc = item.video;
          //   if (!item.video.startsWith('http')) {
          //     videoSrc = require(`../video/${item.video}`);
          //   }
          //   item.media = { type: 'VIDEO', source: { url: videoSrc } }
          // }

          //TODO change it to read more button, going to other pages
          const handleLinkClick = (
            event: React.SyntheticEvent,
            url: string
          ) => {
            event.preventDefault();
            event.stopPropagation();

            if (/^(https?:)?\/\//i.test(url)) {
              window.open(url, "_blank", "noopener,noreferrer");
              return;
            }

            navigate(localizePath(url.startsWith("/") ? url : `/${url}`));
          };

          const stopPointerPropagation = (event: React.SyntheticEvent) => {
            event.stopPropagation();
          };
          //TODO change it to read more button, going to other pages
          if (hasLink) {
            const links = (item.link || "").split(",");
            const linkButtons = links.map((link, index) => {
              const trimmedLink = link.trim();
              return (
                <Button
                  key={index}
                  onClick={(event) => handleLinkClick(event, trimmedLink)}
                  onMouseDown={stopPointerPropagation}
                  onTouchStart={stopPointerPropagation}
                  size="sm"
                  colorScheme="primary"
                  variant="link"
                  className="timeline-read-more"
                  mt={3}
                  mr={2}
                  style={{
                    zIndex: 1000,
                    position: "relative",
                  }}
                >
                  <span>
                    {t("news.readMore")} {links.length > 1 ? `0${index + 1}` : ""}
                  </span>
                  <span aria-hidden="true" className="timeline-read-more-arrow">
                    →
                  </span>
                </Button>
              );
            });
            elements.push(
              <span key="links" style={{ display: "block" }}>
                {linkButtons}
              </span>
            );
          }

          return {
            ...item,
            media,
            cardDetailedText: (
              <span className="timeline-card-details-content">
                {elements}
              </span>
            ),
          };
        }

        return item;
      }),
    [items, navigate, t, localizePath, siteLocale]
  );

  useEffect(() => {
    const timelineElement = timelineRef.current;
    if (!timelineElement) return;

    if (shouldReduceMotion || !("IntersectionObserver" in window)) {
      const showAllCards = () => {
        timelineElement
          .querySelectorAll<HTMLElement>(".timeline-reveal-card")
          .forEach((card, index) => {
            revealedCardIndexesRef.current.add(index);
            if (!card.classList.contains("timeline-reveal-visible")) {
              card.classList.add("timeline-reveal-visible");
            }
          });
      };

      showAllCards();
      const mutationObserver = new MutationObserver(showAllCards);
      mutationObserver.observe(timelineElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });

      return () => mutationObserver.disconnect();
    }

    if (!("MutationObserver" in window)) {
      timelineElement
        .querySelectorAll<HTMLElement>(".timeline-reveal-card")
        .forEach((card, index) => {
          revealedCardIndexesRef.current.add(index);
          if (!card.classList.contains("timeline-reveal-visible")) {
            card.classList.add("timeline-reveal-visible");
          }
        });
      return;
    }

    timelineElement.classList.add("timeline-reveal-ready");
    const observedCards = new WeakSet<HTMLElement>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const card = entry.target as HTMLElement;
          const cards = Array.from(
            timelineElement.querySelectorAll<HTMLElement>(
              ".timeline-reveal-card"
            )
          );
          const cardIndex = cards.indexOf(card);

          if (cardIndex >= 0) {
            revealedCardIndexesRef.current.add(cardIndex);
          }
          card.classList.add("timeline-reveal-visible");
          observer.unobserve(card);
          observedCards.delete(card);
        });
      },
      {
        threshold: 0.12,
        rootMargin: "0px 0px -10% 0px",
      }
    );

    const observeCards = () => {
      timelineElement
        .querySelectorAll<HTMLElement>(".timeline-reveal-card")
        .forEach((card, index) => {
          if (revealedCardIndexesRef.current.has(index)) {
            if (!card.classList.contains("timeline-reveal-visible")) {
              card.classList.add("timeline-reveal-visible");
            }
            return;
          }

          if (observedCards.has(card)) return;

          observedCards.add(card);
          observer.observe(card);
        });
    };

    observeCards();
    const mutationObserver = new MutationObserver(observeCards);
    mutationObserver.observe(timelineElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [processedItems, shouldReduceMotion]);

  useEffect(() => {
    const timelineElement = timelineRef.current;
    if (!timelineElement) return;

    let animationFrame: number | null = null;

    const getRows = () =>
      Array.from(
        timelineElement.querySelectorAll<HTMLElement>(".vertical-item-row")
      );

    const decorateRows = () => {
      getRows().forEach((row) => {
        const pointButton = row.querySelector<HTMLElement>(
          ".timeline-vertical-circle"
        );
        pointButton?.parentElement?.classList.add("timeline-progress-segment");
      });
    };

    const updateProgress = () => {
      animationFrame = null;
      const rows = getRows();

      if (shouldReduceMotion) {
        timelineElement.classList.add("timeline-progress-static");
        rows.forEach((row) => {
          const segment = row.querySelector<HTMLElement>(
            ".timeline-vertical-circle"
          )?.parentElement;
          segment?.style.setProperty("--timeline-segment-progress", "100%");
          segment?.classList.add("timeline-node-visited");
        });
        return;
      }

      const triggerLine = window.innerHeight * 0.62;
      const timelineHasStarted =
        timelineElement.getBoundingClientRect().top <= triggerLine;
      let currentIndex = -1;

      rows.forEach((row, index) => {
        const rect = row.getBoundingClientRect();
        const progress = Math.max(
          0,
          Math.min(1, (triggerLine - rect.top) / Math.max(rect.height, 1))
        );
        const segment = row.querySelector<HTMLElement>(
          ".timeline-vertical-circle"
        )?.parentElement;

        segment?.style.setProperty(
          "--timeline-segment-progress",
          `${progress * 100}%`
        );

        if (timelineHasStarted && progress > 0.12) {
          segment?.classList.add("timeline-node-visited");
        } else if (!timelineHasStarted) {
          segment?.classList.remove("timeline-node-visited");
        }

        if (rect.top + Math.min(rect.height * 0.2, 80) <= triggerLine) {
          currentIndex = index;
        }
      });

      rows.forEach((row, index) => {
        const isCurrent = index === currentIndex;
        row.classList.toggle("timeline-current-row", isCurrent);
        row
          .querySelector<HTMLElement>(".timeline-reveal-card")
          ?.classList.toggle("timeline-current-card", isCurrent);
      });
    };

    const requestProgressUpdate = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(updateProgress);
    };

    const prepareTimeline = () => {
      decorateRows();
      requestProgressUpdate();
    };

    timelineElement.classList.add("timeline-progress-ready");
    prepareTimeline();

    const mutationObserver = new MutationObserver(prepareTimeline);
    mutationObserver.observe(timelineElement, { childList: true, subtree: true });

    const resizeObserver =
      "ResizeObserver" in window
        ? new ResizeObserver(requestProgressUpdate)
        : undefined;
    resizeObserver?.observe(timelineElement);

    if (!shouldReduceMotion) {
      window.addEventListener("scroll", requestProgressUpdate, { passive: true });
      window.addEventListener("resize", requestProgressUpdate);
    }

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener("scroll", requestProgressUpdate);
      window.removeEventListener("resize", requestProgressUpdate);
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, [processedItems, shouldReduceMotion]);

  return (
    <Box
      ref={timelineRef}
      data-testid="timeline-reveal-root"
      onClickCapture={stopTimelineCardInteraction}
      onMouseDownCapture={stopTimelineCardInteraction}
      onPointerDownCapture={stopTimelineCardInteraction}
      onTouchStartCapture={stopTimelineCardInteraction}
    >
      <Chrono
      items={processedItems}
      mode="VERTICAL_ALTERNATING"
      // cardBgColor="#ff00f0"
      // titleColor="#ff0000"
      // titleColorActive="#ff0000"
      // toolbarBgColor="#ff0000"
      // toolbarBtnBgColor="#ff0000"
      // toolbarTextColor="#ff0000"
      // timelineBgColor="#ff0000"
      // scrollable={{ scrollbar: false}}
      // disableScroll={false}
      theme={{
        primary: theme.colors.primary[500],
        secondary: theme.colors.white,
        cardBgColor: theme.colors.white,
        cardForeColor: theme.colors.textColors.primary,
        cardTitleColor: theme.colors.black,
        cardMediaBgColor: theme.colors.white,
        titleColor: theme.colors.primary[500],
        titleColorActive: theme.colors.primary[600],
      }}
      // mediaSettings={{ fit: 'none' }} //'cover', 'contain', 'fill', 'none', 'scale-down'

      fontSizes={{
        cardSubtitle: "1rem",
        cardText: "1rem",
        cardTitle: "1.125rem",
        title: "1rem",
      }}
      // actually this is just for setting the custom class name
      classNames={{
        card: "timeline-detail-text timeline-reveal-card",
        cardMedia: "timeline-media-no-padding",
        cardSubTitle: "timeline-detail-text timeline-card-subtitle",
        cardText: "timeline-detail-text timeline-card-body",
        cardTitle: "timeline-detail-text timeline-card-title",
        controls: "my-timeline-controls",
        title: "timeline-detail-text timeline-date",
        // timelinePoint: "timeline-detail-text",
        // timelineTrack: "timeline-detail-text",
      }}
      // <p class="TimelineContentDetails-sc-d7qjm1-7 EDcGC">Became a member of Dream Impact, established startup </p>

      enableBreakPoint={true}
      responsiveBreakPoint={900}
      scrollable={false}
      cardHeight={120}
      cardWidth={480}
      lineWidth={2}
      timelinePointDimension={14}
      mediaSettings={{ align: "center", fit: "contain" }}
      disableToolbar={true}
      disableNavOnKey={true}
      disableClickOnCircle={true}
      disableInteraction={true}
      disableAutoScrollOnClick={true}
      animation={{
        slideshow: {
          enabled: !shouldReduceMotion,
          duration: 4000,
          type: "fade",
        },
      }}
      display={{ toolbar: { enabled: false } }}
    >
      {/* <div>
        <h4>Custom Content for Event One</h4>
        <p>This is fully custom JSX rendered in the first card.</p>
      </div>
      <div>
        <img src="<url_to_image>" alt="Custom Image" style={{ width: '100%' }} />
        <p>An image in the second card.</p>
      </div> */}
      </Chrono>
    </Box>
  );
};

export default TimelineComponent;

